// Libs
import moment from 'moment';
import { createContext, ReactNode, useEffect, useState, useRef } from 'react';
import { createCallHistory } from '~/thunks/activity/activityThunk';
// Components, Layouts, Pages
// Others
import {
  EMPTY_STRING,
  RING_CENTRAL_TYPE_ACTIVE_CALL_NOTIFY,
  RING_CENTRAL_TYPE_CALL_END_NOTIFY,
  RING_CENTRAL_TYPE_CONNECTED_STATUS,
  RING_CENTRAL_TYPE_CONNECTING_STATUS,
  RING_CENTRAL_TYPE_CONNECTION_STATUS_NOTIFY,
  RING_CENTRAL_TYPE_DISCONNECTED_STATUS,
  RING_CENTRAL_TYPE_LOGIN_ADAPTER,
  RING_CENTRAL_TYPE_LOGIN_STATUS_NOTIFY,
  RING_CENTRAL_TYPE_LOGOUT_ADAPTER,
  RING_CENTRAL_TYPE_NEW_CALL_ADAPTER,
  RING_CENTRAL_TYPE_NEW_SMS_ADAPTER,
  RING_CENTRAL_TYPE_RINGING_TELEPHONY_STATUS,
  RING_CENTRAL_TYPE_WIDGET_FRAME_ADAPTER,
} from '~/utils/constants/common';
import {
  ringCentralClientId,
  ringCentralClientSecret,
  ringCentralEmbeddableUrl,
  ringCentralServer,
} from '~/utils/constants/env';
import { BusinessTypeEnum, ConnectionStatusEnum, DateFormatEnum } from '~/utils/enum/common';
import { IBodyCreateCallHistory } from '~/utils/interface/activity';
import { useAppDispatch } from '~/utils/redux/hooks';
// Styles, images, icons

type RingCentralContextType = {
  executeCall?: (phoneNumber: string, moduleId: string, moduleType: string) => void;
  sendSMS?: (phoneNumber: string) => void;
  connect?: () => void;
  disconnect?: () => void;
  ringCentralStatus: ConnectionStatusEnum;
  setIsJoinPage?: (isLogin: boolean) => void;
  setIsShowWidget?: (isShowWidget: boolean) => void;
  setMinimizedWidget?: (isMinimizedWidget: boolean) => void;
};

const RingCentralContext = createContext<RingCentralContextType>({
  ringCentralStatus: ConnectionStatusEnum.DISCONNECTED,
});

type RingCentralProviderProps = {
  children: ReactNode;
};

const RingCentralProvider = (props: RingCentralProviderProps) => {
  //#region Destructuring Props
  const { children } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [ringCentralStatus, setRingCentralStatus] = useState<ConnectionStatusEnum>(ConnectionStatusEnum.DISCONNECTED);
  const [isJoinPage, setIsJoinPage] = useState<boolean>(false);
  //#endregion Declare State

  const moduleTypeRef = useRef<string>('');
  const moduleIdRef = useRef<string>('');

  //#region Implement Hook
  useEffect(() => {
    if (isJoinPage && ringCentralEmbeddableUrl && ringCentralClientId && ringCentralClientSecret && ringCentralServer) {
      const script = document.createElement('script');
      script.src = `${ringCentralEmbeddableUrl}?clientId=${ringCentralClientId}&clientSecret=${ringCentralClientSecret}&appServer=${ringCentralServer}`;
      const firstScript = document.getElementsByTagName('script')[0];
      firstScript?.parentNode?.insertBefore(script, firstScript);
      window.addEventListener('message', handleMessage);
      return;
    }

    window.removeEventListener('message', handleMessage);
    handleShowWidget(false);
    handleMinimizeWidget(false);
    return () => {
      window.removeEventListener('message', handleMessage);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJoinPage]);

  const handleConvertCallData = (call: any) => {
    try {
      if (!call) return null;

      const start = call.startTime ? moment(call.startTime) : null;
      const end = call.endTime ? moment(call.endTime) : null;

      const convertTime = {
        startDate: start?.format(DateFormatEnum.YYYY_MM_DD),
        endDate: end?.format(DateFormatEnum.YYYY_MM_DD),
        startTime: start?.format(DateFormatEnum.HH_MM_SS),
        endTime: end?.format(DateFormatEnum.HH_MM_SS),
      };

      const durationMs = end && start ? end.diff(start) : 0;

      const duration = moment.utc(durationMs).format('mm:ss');

      return {
        moduleType: moduleTypeRef.current,
        moduleId: moduleIdRef.current,
        from: call.fromNumber || call.from?.phoneNumber || null,
        to: call.to || call.to?.phoneNumber || null,
        startTime: convertTime?.startTime,
        endTime: convertTime?.endTime,
        startDate: convertTime?.startDate,
        endDate: convertTime?.endDate,
        duration,
      };
    } catch (error) {
      console.error('Error handleConvertCallData:', error);
      return null;
    }
  };
  //#endregion Implement Hook

  //#region Handle Function
  const postMessage = (message: Record<string, any>) => {
    const frame = document.querySelector<HTMLIFrameElement>(RING_CENTRAL_TYPE_WIDGET_FRAME_ADAPTER);
    frame?.contentWindow?.postMessage(message, '*');
  };

  const handleMessage = (event: MessageEvent) => {
    const data = event.data;
    if (!data) return;

    switch (data.type) {
      case RING_CENTRAL_TYPE_ACTIVE_CALL_NOTIFY:
        if (data.call.telephonyStatus === RING_CENTRAL_TYPE_RINGING_TELEPHONY_STATUS) {
          handleShowWidget(true);
          handleMinimizeWidget(true);
          return;
        }
        break;
      case RING_CENTRAL_TYPE_CALL_END_NOTIFY:
        const callData = handleConvertCallData(data.call) as IBodyCreateCallHistory;

        dispatch(createCallHistory(callData));
        break;
      case RING_CENTRAL_TYPE_CONNECTION_STATUS_NOTIFY:
        handleShowWidget(true);
        handleMinimizeWidget(false);
        switch (data?.connectionStatus) {
          case RING_CENTRAL_TYPE_CONNECTED_STATUS:
            setRingCentralStatus(ConnectionStatusEnum.CONNECTED);
            break;
          case RING_CENTRAL_TYPE_CONNECTING_STATUS:
            setRingCentralStatus(ConnectionStatusEnum.CONNECTING);
            break;
          case RING_CENTRAL_TYPE_DISCONNECTED_STATUS:
            setRingCentralStatus(ConnectionStatusEnum.DISCONNECTED);
            break;
        }
        break;
      case RING_CENTRAL_TYPE_LOGIN_STATUS_NOTIFY:
        setRingCentralStatus(data?.loggedIn ? ConnectionStatusEnum.CONNECTING : ConnectionStatusEnum.DISCONNECTED);
        break;
      default:
        break;
    }
  };

  const handleCall = (phoneNumber: string, moduleId: string, moduleType: string) => {
    if (ringCentralStatus !== ConnectionStatusEnum.CONNECTED) return;

    moduleTypeRef.current = moduleType;
    moduleIdRef.current = moduleId;

    handleShowWidget(true);
    handleMinimizeWidget(true);
    postMessage({
      type: RING_CENTRAL_TYPE_NEW_CALL_ADAPTER,
      phoneNumber: phoneNumber,
      toCall: true,
    });
  };

  const handleLogin = () => {
    postMessage({
      type: RING_CENTRAL_TYPE_LOGIN_ADAPTER,
    });
  };

  const handleLogout = () => {
    if (ringCentralStatus !== ConnectionStatusEnum.CONNECTED) return;

    postMessage({
      type: RING_CENTRAL_TYPE_LOGOUT_ADAPTER,
    });
  };

  const handleSendSMS = (phoneNumber: string) => {
    if (ringCentralStatus !== ConnectionStatusEnum.CONNECTED) return;

    handleShowWidget(true);
    handleMinimizeWidget(true);
    postMessage({
      type: RING_CENTRAL_TYPE_NEW_SMS_ADAPTER,
      phoneNumber: phoneNumber,
      conversation: true,
    });
  };

  const handleShowWidget = (isShowWidget: boolean) => {
    (window as any)?.RCAdapter?.setClosed(!isShowWidget);
  };

  const handleMinimizeWidget = (isShowWidget: boolean) => {
    (window as any)?.RCAdapter?.setMinimized(!isShowWidget);
  };
  //#endregion Handle Function

  return (
    <RingCentralContext.Provider
      value={{
        ringCentralStatus,
        connect: handleLogin,
        disconnect: handleLogout,
        executeCall: handleCall,
        sendSMS: handleSendSMS,
        setIsJoinPage: setIsJoinPage,
        setIsShowWidget: handleShowWidget,
        setMinimizedWidget: handleMinimizeWidget,
      }}
    >
      {children}
    </RingCentralContext.Provider>
  );
};

export { RingCentralContext, RingCentralProvider };
